import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Target } from "lucide-react";

export default function Auth() {
  const { login, register, startGoogleLogin } = useAuth();
  const { code } = useParams();
  const [mode, setMode] = useState(code ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [invite, setInvite] = useState(code || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (code) {
      setMode("register");
      setInvite(code);
    }
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name, invite.trim() || undefined);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]">
      <div
        className="hidden lg:flex flex-1 relative items-end p-14"
        style={{
          backgroundImage:
            "linear-gradient(to top, rgba(10,10,10,1) 0%, rgba(10,10,10,0.5) 60%, rgba(10,10,10,0.7) 100%), url(https://images.pexels.com/photos/7317341/pexels-photo-7317341.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 flex items-center justify-center bg-[#FF3B30] rounded-sm">
              <Target className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-3xl font-extrabold uppercase tracking-tight text-white">
              Oche<span className="text-[#FF3B30]">One</span>
            </span>
          </div>
          <h1 className="font-display text-6xl xl:text-7xl font-black uppercase leading-[0.9] tracking-tighter text-white max-w-lg">
            Track every <span className="text-[#FFCC00]">throw.</span> Settle every{" "}
            <span className="text-[#FF3B30]">rivalry.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-lg max-w-md font-medium">
            Build your own private club. Invite your mates, hand them a login, and let everyone
            play, practice and climb the leaderboards.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8 justify-center">
            <div className="w-9 h-9 flex items-center justify-center bg-[#FF3B30] rounded-sm">
              <Target className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-extrabold uppercase text-white">
              Oche<span className="text-[#FF3B30]">One</span>
            </span>
          </div>

          <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white">
            {mode === "login" ? "Sign In" : "Create Account"}
          </h2>
          <p className="text-slate-400 mt-1 mb-6 text-sm">
            {invite && mode === "register"
              ? "You've been invited to a club — set up your login."
              : mode === "login"
              ? "Welcome back to the oche."
              : "Start building your darts legacy."}
          </p>

          <button
            data-testid="google-login-btn"
            onClick={() => startGoogleLogin(invite.trim() || undefined)}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-black font-bold py-3 rounded-sm transition-colors duration-150 mb-4"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-[#262626]" />
            <span className="text-xs font-bold uppercase text-slate-600">or</span>
            <div className="flex-1 h-px bg-[#262626]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field label="Name">
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] transition-shadow"
                  placeholder="Your name"
                />
              </Field>
            )}
            <Field label={mode === "login" ? "Email or Username" : "Email"}>
              <input
                data-testid="auth-email-input"
                type={mode === "login" ? "text" : "email"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] transition-shadow"
                placeholder={mode === "login" ? "you@example.com or username" : "you@example.com"}
              />
            </Field>
            <Field label="Password">
              <input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] transition-shadow"
                placeholder="••••••••"
              />
            </Field>
            {mode === "register" && (
              <Field label="Invite code (optional)">
                <input
                  data-testid="auth-invite-input"
                  value={invite}
                  onChange={(e) => setInvite(e.target.value.toUpperCase())}
                  className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-[#007AFF] transition-shadow"
                  placeholder="Join a friend's club"
                />
              </Field>
            )}

            {error && (
              <p data-testid="auth-error" className="text-[#FF3B30] text-sm font-medium">
                {error}
              </p>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-60 text-white font-bold uppercase tracking-wide py-3 rounded-sm transition-colors duration-150"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-400 text-center">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle-btn"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-[#007AFF] font-semibold hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          {mode === "login" && (
            <p className="mt-4 text-xs text-slate-600 text-center">
              Sign in to manage your league.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-1.5 block">
        {label}
      </span>
      {children}
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z"
      />
    </svg>
  );
}
